import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Check, ChevronDown, ExternalLink, Headphones, LifeBuoy, Loader2, MessageSquare, MoreVertical, Pause, Play, Send, ThumbsDown, ThumbsUp, Volume2, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
export interface AIChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
}
export interface AIChatPanelProps {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  messages: AIChatMessage[];
  isTyping: boolean;
  inputValue: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onSuggestedQuestion: (question: string) => void;
  suggestedQuestions?: string[];
  assistantName?: string;
  avatarUrl?: string;
  userName?: string;
  greeting?: string;
  timestamp?: string;
  accentColor?: string;
  secondaryColor?: string;
  defaultSize?: {
    width: number;
    height: number;
  };
  minSize?: {
    width: number;
    height: number;
  };
  maxSize?: {
    width: number;
    height: number;
  };
}
const DEFAULT_AVATAR = 'https://cdn.magicpatterns.com/uploads/wx2adF4ofC4ZyYeARfvKrn/image.png';
const DEFAULT_ACCENT = '#DB3725';
const DEFAULT_SECONDARY = '#30718D';
const DEFAULT_SIZE = {
  width: 408,
  height: 634
};
const MIN_SIZE = {
  width: 340,
  height: 420
};
const MAX_SIZE = {
  width: 720,
  height: 900
};
const DEFAULT_SUGGESTIONS = ['What can you help me with?', 'Give me a quick summary', 'What should I focus on?', 'Show me recent activity'];
const TTS_LANGUAGES = ['English', 'Spanish (US)', 'Spanish (Mexico)', 'Spanish (Spain)'];
const TTS_SPEEDS = [0.75, 1, 1.25, 1.5, 2];
const TTS_TRANSCRIPT = 'This is a preview of the read-aloud transcript. The assistant converts the current conversation into spoken audio so you can listen instead of read. You can change the playback speed, switch languages, and follow along with the transcript shown here.';
const SUPPORT_ISSUE_TYPES = ['Badge or certificate', 'Learning content', 'Feedback', 'Other'];
type TTSStage = 'select' | 'generating' | 'playing';
function formatTime(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
function TypingIndicator({
  secondaryColor
}: {
  secondaryColor: string;
}) {
  return <div className="flex items-center gap-1.5 px-4 py-3" data-id="element-70" data-mp="7qmd0iqiwls">
      {[0, 0.15, 0.3].map(delay => <motion.span key={delay} className="h-2 w-2 rounded-full" style={{
      backgroundColor: secondaryColor
    }} animate={{
      y: [0, -6, 0]
    }} transition={{
      duration: 0.6,
      repeat: Infinity,
      delay
    }} data-id="element-71" data-mp="47rd9kn0t09q" />)}
    </div>;
}
function FormattedMessage({
  content,
  secondaryColor
}: {
  content: string;
  secondaryColor: string;
}) {
  return <div className="space-y-1" data-id="element-72" data-mp="2kx6jsecgljke">
      {content.split('\n').map((line, index) => {
      if (!line) return <div key={index} className="h-2" data-id="element-73" data-mp="1233ht29nswgk" />;
      if (line.startsWith('**') && line.endsWith('**')) return <p key={index} className="mb-1 mt-2 font-bold text-gray-900" data-id="element-74" data-mp="x8z5e3hmjw83">
              {line.replace(/\*\*/g, '')}
            </p>;
      if (line.startsWith('•')) return <p key={index} className="flex gap-1.5 text-sm leading-relaxed text-gray-700" data-id="element-75" data-mp="o8ard8s8dvv7">
              <span style={{
          color: secondaryColor
        }} data-id="element-76" data-mp="1gy9olrgkwzay">
                •
              </span>
              <span data-id="element-77" data-mp="1c1sxqphzw6u6">{line.slice(2)}</span>
            </p>;
      return <p key={index} className="text-sm leading-relaxed text-gray-700" data-id="element-78" data-mp="170ddv0rk06dd" data-mpi={index}>
            {line}
          </p>;
    })}
    </div>;
}
function ChatInput({
  inputValue,
  onInputChange,
  onSend,
  isTyping,
  accentColor
}: {
  inputValue: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  isTyping: boolean;
  accentColor: string;
}) {
  const [isFocused, setIsFocused] = useState(false);
  return <div className="relative" data-id="element-79" data-mp="1ahg8039wti3c">
      <input type="text" value={inputValue} onChange={event => onInputChange(event.target.value)} onKeyDown={event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        onSend();
      }
    }} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} placeholder="Ask anything" maxLength={475} className="w-full rounded-full border bg-white py-3 pl-5 pr-12 text-sm text-[#001823] placeholder:text-gray-500 outline-none transition-shadow" style={{
      borderColor: isFocused ? 'transparent' : '#d1d5db',
      boxShadow: isFocused ? `0 0 0 2px ${accentColor}` : undefined
    }} data-id="element-80" data-mp="1ai96kgypt1ae" />
      <button type="button" onClick={onSend} disabled={!inputValue.trim() || isTyping} aria-label="Send message" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-2 text-gray-400 transition-colors hover:text-[#DB3725] disabled:cursor-not-allowed disabled:opacity-40" data-id="element-81" data-mp="mt6s1hzdnok4">
        <Send size={18} data-id="element-82" data-mp="ja7lwtna4268" />
      </button>
    </div>;
}
function AudioPanel({
  onClose,
  accentColor,
  secondaryColor
}: {
  onClose: () => void;
  accentColor: string;
  secondaryColor: string;
}) {
  const [stage, setStage] = useState<TTSStage>('select');
  const [language, setLanguage] = useState(TTS_LANGUAGES[0]);
  const [langOpen, setLangOpen] = useState(false);
  const [showTranscript, setShowTranscript] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [speedOpen, setSpeedOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const generationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const languageRef = useRef<HTMLDivElement>(null);
  const duration = 77;
  useEffect(() => {
    if (!langOpen) return;
    const close = (event: MouseEvent) => {
      if (languageRef.current && !languageRef.current.contains(event.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [langOpen]);
  useEffect(() => {
    if (stage !== 'playing' || !isPlaying) return;
    const id = window.setInterval(() => setProgress(value => value >= duration ? (setIsPlaying(false), duration) : value + speed), 1000);
    return () => window.clearInterval(id);
  }, [stage, isPlaying, speed]);
  useEffect(() => () => {
    if (generationTimer.current) clearTimeout(generationTimer.current);
  }, []);
  const startGenerating = () => {
    setStage('generating');
    generationTimer.current = setTimeout(() => {
      setStage('playing');
      setIsPlaying(true);
      setProgress(2);
    }, 2200);
  };
  const cancelGenerating = () => {
    if (generationTimer.current) clearTimeout(generationTimer.current);
    setStage('select');
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 z-20 flex flex-col bg-white" data-id="element-83" data-mp="3q1tfscg5bii1">
      <header className="flex h-12 flex-shrink-0 items-center justify-between border-b border-gray-100 px-4" data-id="element-84" data-mp="3g68i2qi414e3">
        <div className="flex items-center gap-3" data-id="element-85" data-mp="3b49fhiu72bi7">
          <Headphones size={18} style={{
          color: accentColor
        }} data-id="element-86" data-mp="373h7su0z0eo1" />
          <h3 className="text-sm font-medium text-[#001823]" data-id="element-87" data-mp="3267h7b1uhwym">Audio Player</h3>
        </div>
        <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full text-[#001823] transition-colors hover:bg-gray-100" aria-label="Close audio player" data-id="element-88" data-mp="15bkv3h32gyro">
          <X size={18} data-id="element-89" data-mp="98ytxh3uupyv" />
        </button>
      </header>
      <div className="flex-1 overflow-y-auto px-4 py-4" data-id="element-90" data-mp="e7s0epkq5gpj">
        {stage === 'select' && <div className="space-y-4" data-id="element-91" data-mp="3vo9s7rd2eefs">
            <div data-id="element-92" data-mp="3vn8gdko85nx0">
              <label className="mb-1.5 block text-sm font-medium text-[#001823]" data-id="element-93" data-mp="3vlnmzydncb0q">
                Language
              </label>
              <div className="relative" ref={languageRef} data-id="element-94" data-mp="3mnbnf6usy2w5">
                <button type="button" onClick={() => setLangOpen(open => !open)} aria-haspopup="listbox" aria-expanded={langOpen} className="flex w-full items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-[#001823] transition-colors hover:bg-gray-50" data-id="element-95" data-mp="3mls4f8hrhdqo">
                  <span className="flex-1 text-left" data-id="element-96" data-mp="1avg2xz8az0nz">{language}</span>
                  <ChevronDown size={16} className={`text-gray-500 transition-transform ${langOpen ? 'rotate-180' : ''}`} data-id="element-97" data-mp="1fvavs8bn20bo" />
                </button>
                {langOpen && <div role="listbox" className="absolute left-0 top-full z-10 mt-2 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg" data-id="element-98" data-mp="2vdqn9v5jawr9">
                    {TTS_LANGUAGES.map(lang => <button key={lang} type="button" role="option" aria-selected={language === lang} onClick={() => {
                setLanguage(lang);
                setLangOpen(false);
              }} className={`w-full border-b border-gray-100 px-4 py-3 text-left text-sm transition-colors last:border-b-0 hover:bg-gray-50 ${language === lang ? 'bg-[#30718D]/5 font-semibold text-[#30718D]' : 'text-gray-700'}`} data-id="element-99" data-mp="3j3b5ucxrzs00">
                        {lang}
                      </button>)}
                  </div>}
              </div>
            </div>
            <button onClick={startGenerating} className="flex w-full items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-medium text-white transition-colors" style={{
          backgroundColor: accentColor
        }} data-id="element-100" data-mp="4z1osdpoer0m">
              <Headphones size={16} data-id="element-101" data-mp="sow20wdwqhle" />
              Listen to this page
            </button>
          </div>}
        {stage === 'generating' && <div className="flex h-full flex-col items-center justify-center gap-4 py-6 text-center" data-id="element-102" data-mp="ixrqk6nfxevb">
            <div className="w-full max-w-xs overflow-hidden rounded-full bg-gray-100" data-id="element-103" data-mp="1gcvr7rvklzxv">
              <motion.div className="h-1.5 rounded-full" style={{
            backgroundColor: accentColor
          }} initial={{
            width: '10%'
          }} animate={{
            width: ['10%', '90%']
          }} transition={{
            duration: 2,
            ease: 'easeInOut'
          }} data-id="element-104" data-mp="1bmfds7rr2ebi" />
            </div>
            <div className="flex items-center gap-2 text-sm text-[#001823]" data-id="element-105" data-mp="1xsrjw5gr8hmu">
              <Loader2 size={16} className="animate-spin" style={{
            color: secondaryColor
          }} data-id="element-106" data-mp="22tfltm6iuivu" />
              Generating audio. This may take up to 3 minutes.
            </div>
            <button onClick={cancelGenerating} className="rounded-full border border-gray-300 px-5 py-2 text-sm text-[#001823] transition-colors hover:border-gray-400 hover:bg-gray-50" data-id="element-107" data-mp="2glloq6knytdc">
              Cancel
            </button>
          </div>}
        {stage === 'playing' && <div className="space-y-4" data-id="element-108" data-mp="20vw1hfdagsan">
            <div className="flex items-center gap-3" data-id="element-109" data-mp="1vwb3p89uupzi">
              <button onClick={() => setIsPlaying(playing => !playing)} className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-white" style={{
            backgroundColor: accentColor
          }} aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-110" data-mp="1vxm9fdmv27t9">
                {isPlaying ? <Pause size={16} fill="currentColor" data-id="element-111" data-mp="2elzj1t183xtr" /> : <Play size={16} fill="currentColor" data-id="element-112" data-mp="2tkbg40sid8lg" />}
              </button>
              <span className="flex-shrink-0 text-xs tabular-nums text-gray-500" data-id="element-113" data-mp="2okp1bmx17kaj">
                {formatTime(progress)} / {formatTime(duration)}
              </span>
              <button type="button" onClick={event => {
            const rect = event.currentTarget.getBoundingClientRect();
            setProgress(Math.max(0, Math.min(duration, (event.clientX - rect.left) / rect.width * duration)));
          }} className="relative h-1.5 flex-1 rounded-full bg-gray-100" aria-label="Audio progress" data-id="element-114" data-mp="u6xji04jtu40">
                <span className="absolute left-0 top-0 h-full rounded-full" style={{
              width: `${Math.min(100, progress / duration * 100)}%`,
              backgroundColor: accentColor
            }} data-id="element-115" data-mp="2r5wh41ahk19v" />
              </button>
              <Volume2 size={16} className="flex-shrink-0 text-gray-400" data-id="element-116" data-mp="bdz6nhrm5u29" />
            </div>
            <div className="flex flex-wrap items-center gap-2" data-id="element-117" data-mp="gdvbtkc1rrje">
              <button onClick={() => setShowTranscript(show => !show)} className="rounded-full border border-gray-300 px-3 py-1.5 text-xs text-[#001823] transition-colors hover:border-gray-400 hover:bg-gray-50" data-id="element-118" data-mp="gb915btv0vwy">
                {showTranscript ? 'Hide Transcript' : 'Show Transcript'}
              </button>
              <div className="relative" data-id="element-119" data-mp="1yfno2qrtma8l">
                <button onClick={() => setSpeedOpen(open => !open)} className="flex items-center gap-1 rounded-full border border-gray-300 px-3 py-1.5 text-xs text-[#001823] transition-colors hover:border-gray-400 hover:bg-gray-50" data-id="element-120" data-mp="1yj1ovuku6bdx">
                  Speed {speed}x<ChevronDown size={12} data-id="element-121" data-mp="1jpgb2qkl1jfa" />
                </button>
                {speedOpen && <div className="absolute bottom-full left-0 z-10 mb-1 w-24 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg" data-id="element-122" data-mp="1foebu9qo17db">
                    {TTS_SPEEDS.map(value => <button key={value} onClick={() => {
                setSpeed(value);
                setSpeedOpen(false);
              }} className="w-full px-3 py-2 text-left text-xs hover:bg-gray-50" style={{
                color: value === speed ? accentColor : '#001823',
                fontWeight: value === speed ? 600 : 400
              }} data-id="element-123" data-mp="15ue03p0vp3vm">
                        {value}x
                      </button>)}
                  </div>}
              </div>
              <div className="relative ml-auto" data-id="element-124" data-mp="37nh3w789bf1i">
                <select value={language} onChange={event => setLanguage(event.target.value)} className="appearance-none rounded-full border border-gray-300 bg-white py-1.5 pl-3 pr-7 text-xs text-[#001823] outline-none hover:border-gray-400" data-id="element-125" data-mp="3cgk0ko8bz4lx">
                  {TTS_LANGUAGES.map(lang => <option key={lang} data-id="element-126" data-mp="3n8k865gfmmkx">{lang}</option>)}
                </select>
                <ChevronDown size={12} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-127" data-mp="3i9zcmjc4kr2t" />
              </div>
            </div>
            {showTranscript && <p className="pt-1 text-sm leading-relaxed text-gray-700" data-id="element-128" data-mp="34cu0q2pqcxzn">
                {TTS_TRANSCRIPT}
              </p>}
          </div>}
      </div>
      <footer className="flex-shrink-0 border-t border-gray-100 px-4 py-3" data-id="element-129" data-mp="3dd8qpfdn319h">
        <p className="text-center text-xs leading-relaxed text-gray-500" data-id="element-130" data-mp="39b5ciizl5iki">
          This tool uses AI to convert text to speech and may occasionally
          produce inaccuracies.
        </p>
      </footer>
    </motion.div>;
}
function SupportPanel({
  onClose,
  accentColor,
  secondaryColor
}: {
  onClose: () => void;
  accentColor: string;
  secondaryColor: string;
}) {
  const [sent, setSent] = useState(false);
  const [issueType, setIssueType] = useState('');
  const [typeOpen, setTypeOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [emailCopy, setEmailCopy] = useState(false);
  const [descFocused, setDescFocused] = useState(false);
  const typeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!typeOpen) return;
    const close = (event: MouseEvent) => {
      if (typeRef.current && !typeRef.current.contains(event.target as Node)) setTypeOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [typeOpen]);
  const canSend = issueType !== '' && description.trim() !== '';
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 z-20 flex flex-col bg-white" data-id="element-131" data-mp="1xtjahtys68zx">
      <header className="flex h-12 flex-shrink-0 items-center justify-between border-b border-gray-100 px-4" data-id="element-132" data-mp="2jt9sr3i2ffz">
        <div className="flex items-center gap-3" data-id="element-133" data-mp="3tvml0oi718kb">
          <LifeBuoy size={18} style={{
          color: accentColor
        }} data-id="element-134" data-mp="3puszg1s845ul" />
          <h3 className="text-sm font-medium text-[#001823]" data-id="element-135" data-mp="3kxapq96dtmlj">
            Report an Issue
          </h3>
        </div>
        <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full text-[#001823] transition-colors hover:bg-gray-100" aria-label="Close support" data-id="element-136" data-mp="lgksayb7wqiz">
          <X size={18} data-id="element-137" data-mp="ciiqigjnttun" />
        </button>
      </header>
      <div className="flex-1 overflow-y-auto px-4 py-4" data-id="element-138" data-mp="7g9ypniiwxu4">
        {sent ? <div className="flex h-full flex-col items-center justify-center gap-3 py-6 text-center" data-id="element-139" data-mp="2x76jdu1t78qu">
            <span className="flex h-12 w-12 items-center justify-center rounded-full" style={{
          backgroundColor: `${secondaryColor}1a`
        }} data-id="element-140" data-mp="2s70xyvm4uih7">
              <Check size={24} style={{
            color: secondaryColor
          }} data-id="element-141" data-mp="2n1x8uy2nasl9" />
            </span>
            <h4 className="text-base font-bold text-[#001823]" data-id="element-142" data-mp="2wsjh27818gtx">
              Issue reported
            </h4>
            <p className="max-w-xs text-sm leading-relaxed text-gray-600" data-id="element-143" data-mp="30udbvyvjqqm6">
              Thanks for letting us know. Our team will review your report and
              follow up as soon as possible.
            </p>
            <button onClick={onClose} className="mt-2 rounded-full px-6 py-2.5 text-sm font-medium text-white" style={{
          backgroundColor: accentColor
        }} data-id="element-144" data-mp="3as18yvgmka7i">
              Done
            </button>
          </div> : <div className="space-y-4" data-id="element-145" data-mp="1lee9734p7mtb">
            <p className="text-sm leading-relaxed text-gray-600" data-id="element-146" data-mp="1lfgyx7qabj7n">
              Please provide a brief description of your issue, and we will
              review it as soon as possible.
            </p>
            <div data-id="element-147" data-mp="3tgx8zj2u1po7">
              <label className="mb-1.5 block text-sm font-medium text-[#001823]" data-id="element-148" data-mp="2cp28j60d47e">
                Type of Issue
              </label>
              <div className="relative" ref={typeRef} data-id="element-149" data-mp="73p0brqv5sbh">
                <button type="button" onClick={() => setTypeOpen(open => !open)} aria-haspopup="listbox" aria-expanded={typeOpen} className="flex w-full items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium transition-colors hover:bg-gray-50" data-id="element-150" data-mp="759u3f5ct78a">
                  <span className={`flex-1 text-left ${issueType ? 'text-[#001823]' : 'text-gray-400'}`} data-id="element-151" data-mp="pzx5ywnsg57b">
                    {issueType || 'Select'}
                  </span>
                  <ChevronDown size={16} className={`text-gray-500 transition-transform ${typeOpen ? 'rotate-180' : ''}`} data-id="element-152" data-mp="2ka51hzc12t9b" />
                </button>
                {typeOpen && <div role="listbox" className="absolute left-0 top-full z-10 mt-2 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg" data-id="element-153" data-mp="2aldd2166er35">
                    {SUPPORT_ISSUE_TYPES.map(type => <button key={type} type="button" role="option" aria-selected={issueType === type} onClick={() => {
                setIssueType(type);
                setTypeOpen(false);
              }} className={`w-full border-b border-gray-100 px-4 py-3 text-left text-sm transition-colors last:border-b-0 hover:bg-gray-50 ${issueType === type ? 'bg-[#30718D]/5 font-semibold text-[#30718D]' : 'text-gray-700'}`} data-id="element-154" data-mp="2y9edsnx45380">
                        {type}
                      </button>)}
                  </div>}
              </div>
            </div>
            <div data-id="element-155" data-mp="1ac5d283tun7b">
              <label className="mb-1.5 block text-sm font-medium text-[#001823]" data-id="element-156" data-mp="1abm075t1ap05">
                Describe Issue <span className="text-[#DB3725]" data-id="element-157" data-mp="wk77265ex7fd">*</span>
              </label>
              <textarea value={description} onChange={event => setDescription(event.target.value)} onFocus={() => setDescFocused(true)} onBlur={() => setDescFocused(false)} rows={4} placeholder="Tell us what went wrong…" className="w-full resize-none rounded-lg border bg-white px-4 py-3 text-sm text-[#001823] placeholder:text-gray-400 outline-none transition-shadow" style={{
            borderColor: descFocused ? 'transparent' : '#d1d5db',
            boxShadow: descFocused ? `0 0 0 2px ${accentColor}` : undefined
          }} data-id="element-158" data-mp="11bgvx4j506q7" />
            </div>
            <label className="flex cursor-pointer items-start gap-2.5" data-id="element-159" data-mp="81fa3br97jsd">
              <input type="checkbox" checked={emailCopy} onChange={event => setEmailCopy(event.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300" style={{
            accentColor
          }} data-id="element-160" data-mp="30hqcuqlihor" />
              <span className="text-sm leading-relaxed text-gray-600" data-id="element-161" data-mp="qp254n42ay2d">
                I would like to receive a confirmation email to the address
                connected to this account.
              </span>
            </label>
          </div>}
      </div>
      {!sent && <footer className="flex flex-shrink-0 items-center justify-end gap-3 border-t border-gray-100 px-4 py-3" data-id="element-162" data-mp="vqhpecyf10jp">
          <button onClick={onClose} className="rounded-full border px-5 py-2.5 text-sm font-medium transition-colors hover:bg-gray-50" style={{
        borderColor: accentColor,
        color: accentColor
      }} data-id="element-163" data-mp="1fqrkwykwur9i">
            Cancel
          </button>
          <button onClick={() => setSent(true)} disabled={!canSend} className="rounded-full px-6 py-2.5 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40" style={{
        backgroundColor: accentColor
      }} data-id="element-164" data-mp="1ti6bbqh5duql">
            Send
          </button>
        </footer>}
    </motion.div>;
}
function ChatWindow({
  messages,
  isTyping,
  inputValue,
  onInputChange,
  onSend,
  onSuggestedQuestion,
  onClose,
  suggestedQuestions,
  assistantName,
  avatarUrl,
  userName,
  greeting,
  timestamp,
  accentColor,
  secondaryColor
}: Omit<AIChatPanelProps, 'isOpen' | 'onOpen' | 'defaultSize' | 'minSize' | 'maxSize'> & {
  suggestedQuestions: string[];
  assistantName: string;
  avatarUrl: string;
  userName: string;
  greeting: string;
  timestamp: string;
  accentColor: string;
  secondaryColor: string;
}) {
  const [audioOpen, setAudioOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [messageFeedback, setMessageFeedback] = useState<Record<number, 'up' | 'down'>>({});
  const endRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const latestBlockRef = useRef<HTMLDivElement>(null);
  const isEmpty = messages.length === 0 && !isTyping;
  // Bring the newest message to the TOP of the thread so long answers are read
  // from their first line instead of landing at the end
  useEffect(() => {
    const container = scrollAreaRef.current;
    const target = latestBlockRef.current;
    if (!container || !target) return;
    const offset = target.getBoundingClientRect().top - container.getBoundingClientRect().top;
    container.scrollTo({
      top: Math.max(0, container.scrollTop + offset - 8),
      behavior: 'smooth'
    });
  }, [messages, isTyping]);
  return <div className="relative flex h-full w-full flex-col overflow-hidden rounded-t-2xl border border-b-0 border-gray-200 bg-white font-['Roboto'] shadow-[0px_4px_24px_rgba(0,0,0,0.12)]" data-id="element-165" data-mp="2rtw3b67d35uo">
      <AnimatePresence data-id="element-166" data-mp="3kia5zz9eaqck">
        {audioOpen && <AudioPanel onClose={() => setAudioOpen(false)} accentColor={accentColor} secondaryColor={secondaryColor} data-id="element-167" data-mp="3kffhnwn9gldv" />}
        {supportOpen && <SupportPanel onClose={() => setSupportOpen(false)} accentColor={accentColor} secondaryColor={secondaryColor} data-id="element-168" data-mp="3fi5ms1h3rhin" />}
      </AnimatePresence>
      <header className="flex h-12 flex-none items-center justify-between border-b border-gray-100 px-4" data-id="element-169" data-mp="2430i4rxev8we">
        <div className="flex items-center gap-3" data-id="element-170" data-mp="1wjhjxeflj938">
          <MessageSquare size={18} fill={accentColor} stroke={accentColor} data-id="element-171" data-mp="1whwlveu2p80m" />
          <h3 className="text-sm font-medium text-[#001823]" data-id="element-172" data-mp="1rl6a33pawyl0">Chat</h3>
        </div>
        <div className="flex items-center gap-1" data-id="element-173" data-mp="26i71f63kyriy">
          <button type="button" onClick={() => setAudioOpen(true)} aria-label="Read aloud with text to speech" className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100" data-id="element-174" data-mp="26gvwh2yefdq9">
            <Headphones size={18} data-id="element-175" data-mp="18uoi51cam70p" />
          </button>
          <button type="button" onClick={() => setSupportOpen(true)} aria-label="Report an issue" className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100" data-id="element-176" data-mp="18rslrhg3fqed">
            <LifeBuoy size={18} data-id="element-177" data-mp="v1p3f1kc4gu7" />
          </button>
          <button type="button" aria-label="More options" className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100" data-id="element-178" data-mp="v0dy2xb8r10z">
            <MoreVertical size={18} data-id="element-179" data-mp="4lx5qtgzej09" />
          </button>
          <button type="button" onClick={onClose} aria-label="Minimize chat" className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100" data-id="element-180" data-mp="9idv1qzx33ex">
            <ChevronDown size={18} data-id="element-181" data-mp="ikn2u93swiom" />
          </button>
        </div>
      </header>
      <div ref={scrollAreaRef} className="flex-1 overflow-y-auto px-4" style={isEmpty ? {
      background: 'linear-gradient(90deg, #E3F1F7 0%, #FFFFFF 50%, #E3F1F7 100%)'
    } : undefined} data-id="element-182" data-mp="njq6pq81oiv0">
        {isEmpty ? <div className="flex h-full flex-col items-center justify-center px-2 text-center" data-id="element-183" data-mp="1271klyrp8eya">
            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-[#D9EEF2]" data-id="element-184" data-mp="16b0z6btf53p0">
              <img src={avatarUrl} alt={`${assistantName}, your AI assistant`} className="h-full w-full object-cover" data-id="element-185" data-mp="1b6gaaw01klhq" />
            </div>
            <p className="mt-3 text-sm font-bold text-gray-600" data-id="element-186" data-mp="2qtl7xqw1qyvv">
              {assistantName}
            </p>
            <h2 className="mt-2 text-2xl font-bold text-[#001823]" data-id="element-187" data-mp="2mrq7eolxwpj5">
              {greeting}
            </h2>
            <div className="mt-6 w-full" data-id="element-188" data-mp="3eh1ojw9y956z">
              <ChatInput inputValue={inputValue} onInputChange={onInputChange} onSend={onSend} isTyping={isTyping} accentColor={accentColor} data-id="element-189" data-mp="3efrox2ng7oy8" />
            </div>
            <div className="mt-5 flex flex-wrap justify-center gap-2.5" data-id="element-190" data-mp="30po7qq3felfn">
              {suggestedQuestions.slice(0, 4).map((question, index) => <motion.button key={question} type="button" initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.06
          }} onClick={() => onSuggestedQuestion(question)} className="rounded-full border border-gray-300 px-4 py-2 text-sm text-[#001823] transition-colors hover:border-gray-400 hover:bg-gray-50" data-id="element-191" data-mp="20fyso6qxotn" data-mpi={index}>
                  {question}
                </motion.button>)}
            </div>
          </div> : <div className="space-y-4 py-4" data-id="element-192" data-mp="hrx415xhe1ua">
            {messages.map((message, messageIndex) => <div key={message.id} ref={!isTyping && messageIndex === messages.length - 1 ? latestBlockRef : undefined} className="space-y-1" data-id="element-193" data-mp="crsn5esnv53j" data-mpi={messageIndex}>
                {message.role === 'assistant' && <div className="mb-1 flex items-center gap-2" data-id="element-194" data-mp="16cs33iq2h92d">
                    <div className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full bg-[#D9EEF2]" data-id="element-195" data-mp="1ae58xst5tc9a">
                      <img src={avatarUrl} alt={assistantName} className="h-full w-full object-cover" data-id="element-196" data-mp="wdawmz2zqr5c" />
                    </div>
                    <span className="text-xs font-bold text-gray-600" data-id="element-197" data-mp="mmzu9gyygq0k">
                      {assistantName}
                    </span>
                  </div>}
                <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.2
          }} className={message.role === 'user' ? 'ml-auto max-w-[85%]' : 'max-w-[90%]'} data-id="element-198" data-mp="dow6ee0hzh5l">
                  <div className={`rounded-2xl px-4 py-3 ${message.role === 'user' ? 'bg-[#F1F3F4] text-[#001823]' : 'border border-gray-200 bg-white'}`} data-id="element-199" data-mp="3gwhltbrmx2ph">
                    {message.role === 'user' ? <p className="text-sm text-[#001823]" data-id="element-200" data-mp="8j0tvsxkgzug">
                        {message.content}
                      </p> : <FormattedMessage content={message.content} secondaryColor={secondaryColor} data-id="element-201" data-mp="3hlby9qor9ga" />}
                  </div>
                  <div className={`mt-1.5 flex items-center px-1 ${message.role === 'user' ? 'justify-end' : 'gap-3'}`} data-id="element-202" data-mp="2jask3hci0mu0">
                    <span className="text-xs text-gray-500" data-id="element-203" data-mp="2ea5zk8ios9lb">{timestamp}</span>
                    {message.role === 'assistant' && <div className="flex items-center gap-1.5" data-id="element-204" data-mp="ysrhklq5rrlc">
                        <span className="text-xs text-gray-400" data-id="element-205" data-mp="ytb3semud256">
                          {messageFeedback[message.id] ? 'Thanks for your feedback' : 'Helpful?'}
                        </span>
                        <button type="button" onClick={() => setMessageFeedback(feedback => ({
                  ...feedback,
                  [message.id]: 'up'
                }))} aria-label="This response was helpful" aria-pressed={messageFeedback[message.id] === 'up'} className={`flex h-6 w-6 items-center justify-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#30718D] ${messageFeedback[message.id] === 'up' ? 'bg-[#007558]/10 text-[#007558]' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'}`} data-id="element-206" data-mp="18k5mj90vpjlv">
                          <ThumbsUp size={14} fill={messageFeedback[message.id] === 'up' ? 'currentColor' : 'none'} aria-hidden="true" data-id="element-207" data-mp="1nk9jgmlz37dr" />
                        </button>
                        <button type="button" onClick={() => setMessageFeedback(feedback => ({
                  ...feedback,
                  [message.id]: 'down'
                }))} aria-label="This response was not helpful" aria-pressed={messageFeedback[message.id] === 'down'} className={`flex h-6 w-6 items-center justify-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#30718D] ${messageFeedback[message.id] === 'down' ? 'bg-[#DB3725]/10 text-[#DB3725]' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'}`} data-id="element-208" data-mp="1inj2zy69a9rt">
                          <ThumbsDown size={14} fill={messageFeedback[message.id] === 'down' ? 'currentColor' : 'none'} aria-hidden="true" data-id="element-209" data-mp="l4q3436392q2" />
                        </button>
                      </div>}
                    {message.role === 'user' && <span className="text-xs font-medium text-gray-600" data-id="element-210" data-mp="brr1lpxdq4p3">
                        {userName}
                      </span>}
                  </div>
                </motion.div>
              </div>)}
            {isTyping && <div ref={latestBlockRef} className="space-y-1" data-id="element-211" data-mp="1z37j2xsaeu2">
                <div className="mb-1 flex items-center gap-2" data-id="element-212" data-mp="prjo191vhyr9">
                  <div className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full bg-[#D9EEF2]" data-id="element-213" data-mp="uqmqclqgxqvj">
                    <img src={avatarUrl} alt={assistantName} className="h-full w-full object-cover" data-id="element-214" data-mp="frsupap1tpsr" />
                  </div>
                  <span className="text-xs font-bold text-gray-600" data-id="element-215" data-mp="30hi59bipypfn">
                    {assistantName}
                  </span>
                </div>
                <div className="max-w-[90%] rounded-2xl border border-gray-200 bg-white" data-id="element-216" data-mp="1bz5wi84rjwf1">
                  <TypingIndicator secondaryColor={secondaryColor} data-id="element-217" data-mp="170cf44mcvl9l" />
                </div>
              </div>}
            <div ref={endRef} data-id="element-218" data-mp="1292vplrehe65" />
            {!isTyping && <div className="flex flex-wrap justify-center gap-2.5 pt-2" data-id="element-219" data-mp="1279kdt5ue5rt">
                {suggestedQuestions.slice(0, 4).map(question => <button key={question} type="button" onClick={() => onSuggestedQuestion(question)} className="rounded-full border border-gray-300 px-4 py-2 text-sm text-[#001823] transition-colors hover:border-gray-400 hover:bg-gray-50" data-id="element-220" data-mp="sboli3w2llma">
                    {question}
                  </button>)}
              </div>}
          </div>}
      </div>
      {!isEmpty && <div className="flex-none px-4 pt-3" data-id="element-221" data-mp="28qpz7r3bgbuy">
          <ChatInput inputValue={inputValue} onInputChange={onInputChange} onSend={onSend} isTyping={isTyping} accentColor={accentColor} data-id="element-222" data-mp="28nvdzxchan0h" />
        </div>}
      <footer className="mt-3 flex-none border-t border-gray-100 px-4 py-3 text-center text-xs leading-relaxed text-gray-500" data-id="element-223" data-mp="2pyh3jkxf7nfq">
        Chats may be monitored, stored, and used as described in our Privacy
        Policy. By continuing, you agree to our{' '}
        <a href="#" className="inline-flex items-center gap-0.5 underline" style={{
        color: secondaryColor
      }} data-id="element-224" data-mp="1xcfvo5m8u7sb">
          Terms &amp; Conditions <ExternalLink size={11} data-id="element-225" data-mp="2777vlg3at6q9" />
        </a>{' '}
        and acknowledge our{' '}
        <a href="#" className="inline-flex items-center gap-0.5 underline" style={{
        color: secondaryColor
      }} data-id="element-226" data-mp="2c96vuh3qrniz">
          Privacy Policy <ExternalLink size={11} data-id="element-227" data-mp="3nlg2y3fhz9hb" />
        </a>
        .
      </footer>
    </div>;
}
export function AIChatPanel({
  isOpen,
  onOpen,
  onClose,
  messages,
  isTyping,
  inputValue,
  onInputChange,
  onSend,
  onSuggestedQuestion,
  suggestedQuestions = DEFAULT_SUGGESTIONS,
  assistantName = 'Phoebe - AI assistant',
  avatarUrl = DEFAULT_AVATAR,
  userName = 'You',
  greeting = 'Hi there, how can I help?',
  timestamp = '11:46 am',
  accentColor = '#DB3725',
  secondaryColor = '#30718D',
  defaultSize = {
    width: 408,
    height: 634
  },
  minSize = {
    width: 340,
    height: 420
  },
  maxSize = {
    width: 720,
    height: 900
  }
}: AIChatPanelProps) {
  const [size, setSize] = useState(defaultSize);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStart = useRef<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const clamp = useCallback((width: number, height: number) => ({
    width: Math.max(minSize.width, Math.min(width, maxSize.width, window.innerWidth - 48)),
    height: Math.max(minSize.height, Math.min(height, maxSize.height, window.innerHeight - 120))
  }), [minSize, maxSize]);
  useEffect(() => {
    if (!isResizing) return;
    const move = (event: PointerEvent) => {
      const start = resizeStart.current;
      if (start) setSize(clamp(start.width + start.x - event.clientX, start.height + start.y - event.clientY));
    };
    const up = () => setIsResizing(false);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [isResizing, clamp]);
  return <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end sm:bottom-6 sm:right-6" data-id="element-228" data-mp="2j9xv2xb5tqvf">
      <AnimatePresence data-id="element-229" data-mp="2o8isyq2xvygp">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        duration: isResizing ? 0 : 0.2
      }} style={{
        width: size.width,
        height: size.height,
        userSelect: isResizing ? 'none' : undefined
      }} className="relative mb-4 max-h-[calc(100vh-120px)] max-w-[calc(100vw-48px)] origin-bottom-right max-sm:!h-[calc(100dvh-104px)] max-sm:!w-[calc(100vw-32px)]" data-id="element-230" data-mp="2o89455tmrxcl">
            <div onPointerDown={event => {
          event.preventDefault();
          resizeStart.current = {
            x: event.clientX,
            y: event.clientY,
            width: size.width,
            height: size.height
          };
          setIsResizing(true);
        }} role="separator" aria-label="Resize chat window" className="absolute -left-1 -top-1 z-10 hidden h-5 w-5 cursor-nwse-resize sm:block" data-id="element-231" data-mp="svq0w0fvzfi" />
            <ChatWindow messages={messages} isTyping={isTyping} inputValue={inputValue} onInputChange={onInputChange} onSend={onSend} onSuggestedQuestion={onSuggestedQuestion} onClose={onClose} suggestedQuestions={suggestedQuestions} assistantName={assistantName} avatarUrl={avatarUrl} userName={userName} greeting={greeting} timestamp={timestamp} accentColor={accentColor} secondaryColor={secondaryColor} data-id="element-232" data-mp="3d9bzr3b6p711" />
          </motion.div>}
      </AnimatePresence>
      {!isOpen && <motion.button type="button" initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} whileHover={{
      scale: 1.05
    }} whileTap={{
      scale: 0.95
    }} onClick={onOpen} className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-[#D9EEF2] shadow-lg transition-shadow hover:shadow-xl" aria-label="Open AI Assistant" data-id="element-233" data-mp="bz362col10ck">
          <img src={avatarUrl} alt="Open AI Assistant" className="h-full w-full object-cover" data-id="element-234" data-mp="znw1m81al66n" />
        </motion.button>}
    </div>;
}